import React, { useEffect, useState } from 'react';
import { useSelector, connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { isIOS } from 'react-device-detect';
import { CFCProps, SCREEN_SIZE, APP_URL } from 'types';
import { RootState } from 'redux/root.reducer';
import { GenericPageLayout } from 'layouts/genericPage.layout';
import {
  Button,
  Link,
  Loading,
  InlineNotification,
  InlineLoading
} from 'carbon-components-react';
import { DocumentPdf24, Zip16, ChevronRight16 } from '@carbon/icons-react';
import {
  fetchResponseAttachments,
  getResponseAttachmentDownloadLink,
  getResponseAttachmentsDownloadAll
} from 'redux/response/actions';
import {
  toLocalDateTimeFormat,
  toLocalDateFormat
} from 'utilities/helper.util';
import {
  selectResponseInfo,
  selecteResponseLoading,
  selectResponseError,
  selectResponseAttachmentDownloadLink,
  selectResponseAttachmentDownloadLinkLoading,
  selectResponseAttachmentsDownloadAll,
  selectResponseAttachmentsDownloadAllLoading
} from 'redux/response/selectors';
import { selectScreenSize } from 'redux/session/selectors';
import ErrorPage from './errorPage';

export const Response: React.FC<Props & RouteComponentProps<{}, any, any>> = (
  props: Props
) => {
  const { token } = useParams<any>();
  const dispatch = useDispatch();
  const { session } = props;
  const { currentUser } = session;
  const responseInfo: any = useSelector(selectResponseInfo);
  const responseLoading: boolean = useSelector(selecteResponseLoading);
  const responseError: any = useSelector(selectResponseError);
  const downloadLink: any = useSelector(selectResponseAttachmentDownloadLink);
  const downloadLinkLoading: boolean = useSelector(
    selectResponseAttachmentDownloadLinkLoading
  );
  const downloadAll: any = useSelector(selectResponseAttachmentsDownloadAll);
  const downloadAllLoading: boolean = useSelector(
    selectResponseAttachmentsDownloadAllLoading
  );
  const screenSize = useSelector(selectScreenSize);
  const [filesList, setFilesList] = useState<Array<any>>([]);
  const [enquiryInfo, setEnquiryInfo] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showErrorPage, setShowErrorPage] = useState<boolean>(false);

  const handleFileClick = (fileId: number) => {
    dispatch(
      getResponseAttachmentDownloadLink({
        token,
        fileId,
        userId: currentUser?.id
      })
    );
  };

  const handleButtonClick = () => {
    dispatch(
      getResponseAttachmentsDownloadAll({ token, userId: currentUser?.id })
    );
  };

  useEffect(() => {
    if (token) {
      dispatch(fetchResponseAttachments({ token, userId: currentUser?.id }));
    }
  }, [token, currentUser, dispatch]);

  useEffect(() => {
    if (responseInfo && !responseLoading) {
      const enquiryInfoData = {
        orgName: responseInfo.Enquiry.Organisation.name,
        jobId: responseInfo.Enquiry.externalId,
        jobStart: toLocalDateFormat(responseInfo.Enquiry.digStartAt),
        jobEnd: toLocalDateFormat(responseInfo.Enquiry.digEndAt),
        lodgedAt: toLocalDateTimeFormat(responseInfo.Enquiry.createdAt),
        mapImageURL: responseInfo.Enquiry.MapImageFile.url,
        expiredAt: responseInfo.Enquiry.expiredAt,
        expired: responseInfo.Enquiry.expired
      };
      setEnquiryInfo(enquiryInfoData);
      setFilesList(responseInfo.Files);
      setLoading(false);
    }
    if (responseError && !responseLoading) {
      setLoading(false);
      setShowErrorPage(true);
    }
  }, [responseInfo, responseLoading, responseError]);

  useEffect(() => {
    if (downloadLink && !downloadLinkLoading) {
      const downloadFileName = downloadLink.name;
      const element = document.createElement('a');
      element.href = downloadLink.url;
      element.download = downloadFileName;
      document.body.appendChild(element);
      if (downloadLink !== '') element.click();
    }
  }, [downloadLink, downloadLinkLoading]);

  useEffect(() => {
    if (downloadAll && !downloadAllLoading) {
      const downloadFileName = downloadAll.name;
      const element = document.createElement('a');
      element.href = downloadAll.downloadURL;
      element.download = downloadFileName;
      document.body.appendChild(element);
      if (downloadAll !== '') element.click();
    }
  }, [downloadAll, downloadAllLoading]);

  const buttonDownloadAll = 'Download all';

  return (
    <>
      <div id="response" className="response" data-testid="response">
        {loading && (
          <Loading
            description="Loading response attachments"
            withOverlay={true}
          />
        )}

        {screenSize !== SCREEN_SIZE.SM && enquiryInfo && (
          <InlineNotification
            kind={'warning'}
            title={''}
            subtitle={`The data on this page ${
              enquiryInfo.expired ? 'expired' : 'is valid until'
            }  ${enquiryInfo?.expiredAt}`}
            lowContrast={true}
            hideCloseButton={true}
          />
        )}

        <GenericPageLayout
          pageClassName="response--wrapper"
          isOTPActivated={false}
          showOTPNotification={false}
          showTopLinks={false}
        >
          {!loading && enquiryInfo && (
            <div className="response--body-wrapper">
              <div className="enquiry-info">
                <div className="title">
                  {enquiryInfo.expired
                    ? `Sorry, the response information has expired`
                    : `Your attachments from ${enquiryInfo.orgName}`}
                </div>
                <div className="details">
                  <div
                    className={`job-id ${
                      enquiryInfo.expired ? 'expired-job-id' : ''
                    }`}
                  >
                    Job {enquiryInfo.jobId}
                  </div>
                  <div
                    className={`job-dates ${
                      enquiryInfo.expired ? 'expired-job-dates' : ''
                    }`}
                  >
                    Job dates {enquiryInfo.jobStart}
                    {isIOS ? (
                      <span className="arrow-symbol">&#8594;</span>
                    ) : (
                      ' ⟶'
                    )}{' '}
                    {enquiryInfo.jobEnd}
                  </div>
                  <div
                    className={`lodged-date ${
                      enquiryInfo.expired ? 'expired-lodged-date' : ''
                    }`}
                  >
                    Enquiry date {enquiryInfo.lodgedAt}
                  </div>
                </div>
              </div>
              <div className="enquiry-files-wrapper">
                <div className="map-image-container">
                  <img src={enquiryInfo.mapImageURL} alt="" />
                  <div>
                    {enquiryInfo.expired ? (
                      <Button
                        renderIcon={ChevronRight16}
                        href={APP_URL.LODGE_NEW_ENQUIRY}
                        target={'_blank'}
                      >
                        New enquiry
                      </Button>
                    ) : (
                      <Button
                        renderIcon={Zip16}
                        onClick={() => handleButtonClick()}
                        disabled={downloadAllLoading}
                      >
                        {!downloadAllLoading ? (
                          buttonDownloadAll
                        ) : (
                          <InlineLoading
                            description={'Downloading ...'}
                            status={'active'}
                            aria-live={'assertive'}
                          />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
                <div
                  className={`enquiry-files-container ${
                    enquiryInfo.expired ? 'expired-enquiry-files-container' : ''
                  }`}
                >
                  {filesList.map((file: any, inx: number) => {
                    return (
                      <div key={inx} className="files-list">
                        <a
                          href={`#${file.name}`}
                          className={`icon-link ${
                            enquiryInfo.expired ? 'expired-icon-link' : ''
                          }`}
                        >
                          <DocumentPdf24 />
                        </a>
                        <div className="files-name">
                          <Link
                            href={`#${file.name}`}
                            onClick={() => handleFileClick(file.id)}
                            visited={true}
                            disabled={enquiryInfo.expired}
                          >
                            {file.name}
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </GenericPageLayout>
      </div>
      {!loading && !enquiryInfo && showErrorPage && <ErrorPage />}
    </>
  );
};

const mapStateToProps = ({ app, login, session }: RootState) => ({
  app,
  login,
  session
});

const connector = connect(mapStateToProps);
type Props = CFCProps<typeof connector>;
Response.displayName = 'Response';

export default connector(Response);
